/**
 * @fileoverview Follow-up Field Configurations
 * @module followUpConfigs
 * 
 * Business Context:
 * - Defines follow-up fields for checklist items in Flaws (Step 3) and Maintenance (Step 2)
 * - Captures specific details (county value vs actual value) for stronger appeal packets
 * 
 * Note: Separated from FollowUpFields.tsx component to avoid HMR/Fast Refresh issues
 * when mixing React component exports with non-component exports
 * 
 * Note: Quality and Condition Misclassification flaws now use textbox activity type
 * instead of checklists, so their follow-up configs have been removed.
 */

import { FollowUpConfig } from './FollowUpFields';

/**
 * Follow-up field configurations for Flaw checklists (Step 3)
 * Maps checklist item keys to their follow-up field definitions
 * 
 * Note: QUALITY_MISCLASSIFICATION and CONDITION_MISCLASSIFICATION use
 * the new ClassificationActivity component with textbox input instead
 * of checklist + follow-up fields.
 */
export const FLAW_FOLLOW_UP_CONFIGS: Record<string, FollowUpConfig> = {
  // WRONG_DETAILS
  square_footage_incorrect: {
    key: 'square_footage_incorrect',
    instruction: 'Enter the square footage discrepancy:',
    fields: [
      { name: 'countyValue', label: 'County shows', type: 'number', placeholder: '2400', suffix: 'sq ft' },
      { name: 'actualValue', label: 'Actual', type: 'number', placeholder: '2150', suffix: 'sq ft' }
    ]
  },
  bedroom_count_wrong: {
    key: 'bedroom_count_wrong',
    instruction: 'Enter the bedroom count discrepancy:',
    fields: [
      { name: 'countyValue', label: 'County shows', type: 'number', placeholder: '4', suffix: 'bedrooms' },
      { name: 'actualValue', label: 'Actual', type: 'number', placeholder: '3', suffix: 'bedrooms' }
    ]
  },
  bathroom_count_wrong: {
    key: 'bathroom_count_wrong',
    instruction: 'Enter the bathroom count discrepancy:',
    fields: [
      { name: 'countyValue', label: 'County shows', type: 'text', placeholder: '3', suffix: 'bathrooms' },
      { name: 'actualValue', label: 'Actual', type: 'text', placeholder: '2.5', suffix: 'bathrooms' }
    ]
  },
  year_built_incorrect: {
    key: 'year_built_incorrect',
    instruction: 'Enter the year built discrepancy:',
    fields: [
      { name: 'countyValue', label: 'County shows', type: 'number', placeholder: '1995' },
      { name: 'actualValue', label: 'Actual year', type: 'number', placeholder: '2001' }
    ]
  },
  lot_size_wrong: {
    key: 'lot_size_wrong',
    instruction: 'Enter the lot size discrepancy:',
    fields: [
      { name: 'countyValue', label: 'County shows', type: 'number', placeholder: '8500', suffix: 'sq ft' },
      { name: 'actualValue', label: 'Actual', type: 'number', placeholder: '7200', suffix: 'sq ft' }
    ]
  },
  nonexistent_features: {
    key: 'nonexistent_features',
    instruction: 'What feature(s) are listed that don\'t exist?',
    fields: [
      { name: 'features', label: 'Listed features that don\'t exist', type: 'text', placeholder: 'Pool, detached garage, etc.' }
    ]
  },

  // EQUITY_ISSUES
  similar_homes_lower: {
    key: 'similar_homes_lower',
    instruction: 'Enter a comparable property with lower assessment:',
    fields: [
      { name: 'comparableAddress', label: 'Comparable address', type: 'text', placeholder: '123 Oak St' },
      { name: 'comparableValue', label: 'Their assessed value', type: 'number', placeholder: '285000', prefix: '$' },
      { name: 'yourValue', label: 'Your assessed value', type: 'number', placeholder: '325000', prefix: '$' }
    ]
  },
  per_sqft_higher: {
    key: 'per_sqft_higher',
    instruction: 'Compare the per square foot assessments:',
    fields: [
      { name: 'yourPerSqft', label: 'Your $/sq ft', type: 'number', placeholder: '145', prefix: '$' },
      { name: 'comparablePerSqft', label: 'Comparable $/sq ft', type: 'number', placeholder: '125', prefix: '$' }
    ]
  }
};

/**
 * Follow-up field configurations for Maintenance checklists (Step 2)
 * Enhanced with specific age, material, and measurement fields per the appeal optimization plan
 */
export const MAINTENANCE_FOLLOW_UP_CONFIGS: Record<string, FollowUpConfig> = {
  // Professional opinion - applies to all categories
  professional_opinion: {
    key: 'professional_opinion',
    instruction: 'Who provided the professional assessment?',
    fields: [
      { name: 'professionalName', label: 'Professional/Company name', type: 'text', placeholder: 'ABC Roofing, Smith Plumbing, etc.' },
      { name: 'assessmentDate', label: 'Assessment date', type: 'text', placeholder: 'MM/DD/YYYY' }
    ]
  },

  // =====================
  // ROOF - Enhanced Fields
  // =====================
  age_over_15: {
    key: 'age_over_15',
    instruction: 'Provide specific roof details:',
    fields: [
      { name: 'roofAgeYears', label: 'Roof age', type: 'number', placeholder: '18', suffix: 'years' },
      { name: 'materialType', label: 'Material type', type: 'select', placeholder: 'Select material',
        options: ['Composition/Asphalt Shingles', 'Metal', 'Tile (Clay/Concrete)', 'Wood Shake', 'Slate', 'Flat/Built-up', 'TPO/EPDM', 'Other', 'Unsure'] },
      { name: 'lastReplacementYear', label: 'Last replaced/installed', type: 'number', placeholder: '2006' }
    ]
  },
  hail_damage: {
    key: 'hail_damage',
    instruction: 'Provide hail damage details:',
    fields: [
      { name: 'damageDate', label: 'Approximate date of damage', type: 'text', placeholder: 'MM/YYYY' },
      { name: 'insuranceClaim', label: 'Insurance claim filed?', type: 'select', placeholder: 'Select', options: ['Yes', 'No', 'Pending', 'Unsure'] }
    ]
  },
  visible_damage: {
    key: 'visible_damage',
    instruction: 'Describe the visible damage:',
    fields: [
      { name: 'damageDescription', label: 'Type of damage', type: 'text', placeholder: 'Curling shingles, missing tabs, etc.' },
      { name: 'affectedArea', label: 'Affected area', type: 'select', placeholder: 'Select', options: ['Small (< 10%)', 'Medium (10-30%)', 'Large (30-50%)', 'Extensive (> 50%)', 'Unsure'] }
    ]
  },

  // =====================
  // FOUNDATION - Enhanced Fields
  // =====================
  visible_cracks: {
    key: 'visible_cracks',
    instruction: 'Describe the foundation cracks:',
    fields: [
      { name: 'crackWidthInches', label: 'Largest crack width', type: 'text', placeholder: '0.25', suffix: 'inches' },
      { name: 'crackLength', label: 'Approximate crack length', type: 'text', placeholder: '3', suffix: 'feet' },
      { name: 'crackPattern', label: 'Crack pattern', type: 'select', placeholder: 'Select', options: ['Hairline', 'Stair-step', 'Horizontal', 'Vertical', 'Diagonal', 'Multiple directions', 'Unsure'] }
    ]
  },
  doors_windows_stick: {
    key: 'doors_windows_stick',
    instruction: 'Describe the settling symptoms:',
    fields: [
      { name: 'settlementSeverity', label: 'Severity', type: 'select', placeholder: 'Select', options: ['Mild - occasional sticking', 'Moderate - frequent issues', 'Severe - doors/windows won\'t close', 'Unsure'] },
      { name: 'affectedAreas', label: 'Affected locations', type: 'text', placeholder: 'Master bedroom door, kitchen window, etc.' }
    ]
  },
  uneven_floors: {
    key: 'uneven_floors',
    instruction: 'Describe the floor unevenness:',
    fields: [
      { name: 'slopeEstimate', label: 'Estimated slope', type: 'select', placeholder: 'Select', options: ['Barely noticeable', 'Visible to the eye', 'Ball rolls when placed', 'Significant - furniture tips', 'Unsure'] },
      { name: 'slopeDirection', label: 'Slopes toward', type: 'text', placeholder: 'Northeast corner, center of house, etc.' }
    ]
  },
  pier_beam_issues: {
    key: 'pier_beam_issues',
    instruction: 'Describe pier/beam condition:',
    fields: [
      { name: 'damageType', label: 'Type of damage', type: 'select', placeholder: 'Select', options: ['Wood rot', 'Termite damage', 'Shifted piers', 'Cracked blocks', 'Beam sagging', 'Multiple issues', 'Unsure'] },
      { name: 'affectedPiersEstimate', label: 'Estimated # of affected piers', type: 'number', placeholder: '3' }
    ]
  },

  // =====================
  // HVAC - Enhanced Fields
  // =====================
  age_over_10: {
    key: 'age_over_10',
    instruction: 'Provide specific HVAC system details:',
    fields: [
      { name: 'systemAgeYears', label: 'System age', type: 'number', placeholder: '15', suffix: 'years' },
      { name: 'installYear', label: 'Installation year', type: 'number', placeholder: '2009' },
      { name: 'seerRating', label: 'SEER rating (if known)', type: 'number', placeholder: '13', suffix: 'SEER' },
      { name: 'lastServiceDate', label: 'Last professional service', type: 'text', placeholder: 'MM/YYYY' }
    ]
  },
  high_utility_bills: {
    key: 'high_utility_bills',
    instruction: 'Describe efficiency problems:',
    fields: [
      { name: 'avgSummerBill', label: 'Avg summer electric bill', type: 'number', placeholder: '350', prefix: '$' },
      { name: 'previousYearBill', label: 'Same period last year', type: 'number', placeholder: '250', prefix: '$' }
    ]
  },
  ductwork_issues: {
    key: 'ductwork_issues',
    instruction: 'Describe ductwork problems:',
    fields: [
      { name: 'ductworkType', label: 'Ductwork location', type: 'select', placeholder: 'Select', options: ['Attic', 'Crawl space', 'In walls', 'Combination', 'Unsure'] },
      { name: 'ductworkCondition', label: 'Condition', type: 'select', placeholder: 'Select', options: ['Minor leaks/gaps', 'Significant leaks', 'Disconnected sections', 'Needs replacement', 'Unsure'] }
    ]
  },

  // =====================
  // PLUMBING - Enhanced Fields
  // =====================
  pipe_age: {
    key: 'pipe_age',
    instruction: 'Provide pipe material information:',
    fields: [
      { name: 'supplyPipeMaterial', label: 'Supply pipe material', type: 'select', placeholder: 'Select', options: ['Galvanized steel', 'Copper', 'PEX', 'CPVC', 'Polybutylene (gray)', 'Unknown', 'Mixed'] },
      { name: 'drainPipeMaterial', label: 'Drain pipe material', type: 'select', placeholder: 'Select', options: ['Cast iron', 'PVC', 'ABS', 'Orangeburg', 'Clay/terracotta', 'Unknown', 'Mixed'] },
      { name: 'estimatedPipeAge', label: 'Estimated pipe age', type: 'number', placeholder: '35', suffix: 'years' }
    ]
  },
  water_heater_issues: {
    key: 'water_heater_issues',
    instruction: 'Provide water heater details:',
    fields: [
      { name: 'waterHeaterAgeYears', label: 'Water heater age', type: 'number', placeholder: '12', suffix: 'years' },
      { name: 'waterHeaterType', label: 'Type', type: 'select', placeholder: 'Select', options: ['Tank (gas)', 'Tank (electric)', 'Tankless (gas)', 'Tankless (electric)', 'Heat pump', 'Unsure'] },
      { name: 'tankCapacity', label: 'Tank capacity (if applicable)', type: 'number', placeholder: '50', suffix: 'gallons' }
    ]
  },
  low_water_pressure: {
    key: 'low_water_pressure',
    instruction: 'Describe pressure issues:',
    fields: [
      { name: 'pressureSeverity', label: 'Severity', type: 'select', placeholder: 'Select', options: ['Slight reduction', 'Moderate - affects daily use', 'Severe - barely usable', 'Intermittent', 'Unsure'] },
      { name: 'affectedFixtures', label: 'Affected fixtures', type: 'text', placeholder: 'All faucets, just upstairs, etc.' }
    ]
  },
  visible_leaks: {
    key: 'visible_leaks',
    instruction: 'Describe leak indicators:',
    fields: [
      { name: 'leakType', label: 'Type of evidence', type: 'select', placeholder: 'Select', options: ['Active dripping', 'Water stains', 'Mold/mildew', 'High water bills', 'Foundation cracks from water', 'Multiple signs', 'Unsure'] },
      { name: 'leakLocation', label: 'Location(s)', type: 'text', placeholder: 'Under kitchen sink, bathroom ceiling, etc.' }
    ]
  },

  // =====================
  // ELECTRICAL - Enhanced Fields
  // =====================
  panel_age: {
    key: 'panel_age',
    instruction: 'Provide electrical panel details:',
    fields: [
      { name: 'panelAmperage', label: 'Panel amperage', type: 'select', placeholder: 'Select', options: ['60 amps', '100 amps', '150 amps', '200 amps', '400 amps', 'Unknown'] },
      { name: 'panelBrand', label: 'Panel brand', type: 'select', placeholder: 'Select', options: ['Federal Pacific', 'Zinsco/Sylvania', 'Pushmatic', 'Square D', 'GE', 'Cutler-Hammer/Eaton', 'Murray/Siemens', 'Other/Unknown'] },
      { name: 'lastUpgradeYear', label: 'Last upgrade year (if known)', type: 'number', placeholder: '1985' }
    ]
  },
  two_prong_outlets: {
    key: 'two_prong_outlets',
    instruction: 'Describe outlet issues:',
    fields: [
      { name: 'ungroundedPercentage', label: 'Estimated % of ungrounded outlets', type: 'select', placeholder: 'Select', options: ['A few (< 25%)', 'About half (25-50%)', 'Most (50-75%)', 'All or nearly all (> 75%)', 'Unsure'] },
      { name: 'wiringType', label: 'Wiring type', type: 'select', placeholder: 'Select', options: ['Knob and tube', 'Cloth-covered', 'Aluminum', 'Modern copper/Romex', 'Unknown', 'Mixed'] }
    ]
  },
  frequent_trips: {
    key: 'frequent_trips',
    instruction: 'Describe breaker issues:',
    fields: [
      { name: 'trippingFrequency', label: 'How often?', type: 'select', placeholder: 'Select', options: ['Monthly', 'Weekly', 'Multiple times per week', 'Daily or near-daily', 'Unsure'] },
      { name: 'affectedCircuits', label: 'Which circuits/areas?', type: 'text', placeholder: 'Kitchen, master bedroom, etc.' }
    ]
  }
};




